import { Component, inject, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import {
  ExamInfo,
  Package,
  UserPlanResponse,
} from '../../shared/interfaces/reserve-loading.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { UserPlanRequest } from '../../shared/interfaces/reserve-health.model';
import { Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { NumberCommaPipe } from '../../pipes/number-comma.pipe';
import { NONE_OF_FOLLOWING } from '../../shared/constants/health-questionnaire-fields.constants';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { Decimal } from 'decimal.js';
import { AppointmentDiscountRateService } from '../../shared/services/appointment-discountRate.service';
import { DownloadUserPlanService } from '../../shared/services/download-userPlan.service';
import { DownloadUserPlanRequest } from '../../shared/interfaces/download-userPlan.model';
import { ToastrService } from 'ngx-toastr';
import { IdleTimeoutService } from '../../shared/services/idle-timeout.service';
import moment from 'moment';
import { filenameFromContentDisposition } from '../../shared/utils/file-decode.utils';

/**
 * 專屬方案結果頁面
 */
@Component({
  selector: 'app-reserve-result',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButton,
    MatIconButton,
    NumberCommaPipe,
    NgOptimizedImage,
    NgClass,
  ],
  templateUrl: './reserve-result.component.html',
  styleUrls: ['./reserve-result.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class ReserveResultComponent implements OnInit {
  @ViewChild('video') video!: ElementRef<HTMLVideoElement>;
  private stream: MediaStream | null = null;
  isCameraOpen: boolean = false; // 控制鏡頭是否開啟
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  translateService: TranslateService = inject(TranslateService);
  idleTimeoutService: IdleTimeoutService = inject(IdleTimeoutService);
  downloadUserPlanService: DownloadUserPlanService = inject(DownloadUserPlanService);
  toastr: ToastrService = inject(ToastrService);
  dialog: MatDialog = inject(MatDialog);
  router: Router = inject(Router);
  appointmentDiscountRateService: AppointmentDiscountRateService = inject(AppointmentDiscountRateService);
  partyDiscountRate: string = ''; // 活動折扣率

  allCheckupDiscountRate: string = ''; // 全方位方案折扣率
  aiExclusiveDiscountRate: string = ''; // 智能專屬方案折扣率
  basicCheckupDiscountRate: string = ''; // 基礎方案折扣率

  userPlanResponse?: UserPlanResponse;

  healthPlan = this.sharedStorageService.get('healthPlan');

  // 方案底下所有的健檢項目(全方位/智能專屬/安心起步)
  examInfoGroupByPackage: Array<Array<ExamInfo>> = [[], [], []];

  // 所有風險因子
  allRiskItem: Array<string> = [];

  // 每個類別的examInfo是否顯示全部或收合
  isExamInfoGroupByTypeShowAll: Array<boolean> = [];

  ngOnInit() {
    // 將當前預約步驟設置為3
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 3);
    });

    // 計算方案演算時間
    let calculationStartTime = this.sharedStorageService.get('planCalculationStartTime');
    if (calculationStartTime) {
      console.log('已有紀錄方案演算時間：', new Date(calculationStartTime));
    } else {
      calculationStartTime = this.idleTimeoutService.startCalculateIdleTime();
      console.log('紀錄方案演算時間：', new Date(calculationStartTime));
      this.sharedStorageService.set('planCalculationStartTime', calculationStartTime);
    }

    // 取得活動折扣率
    const userPlan: UserPlanRequest | null =
      this.sharedStorageService.get('userPlanRequest') ?? null;
    if (userPlan?.orgNo) {
      this.appointmentDiscountRateService.getDiscountRates(userPlan.orgNo).subscribe(res => {
        console.log("折扣率:", res.data);
        if (res.data?.discountRate) {
          console.log("partyDiscountRate:", res.data.discountRate);
          this.partyDiscountRate = res.data.discountRate;
          this.sharedStorageService.set('partyDiscountRate', this.partyDiscountRate);
        }
      });
    }

    // 取得上一頁reserve-loading請求的user plan response
    this.userPlanResponse =
      this.sharedStorageService.get('userPlanResponse') ?? undefined;

    // 把所有在本方案內的檢項 添加至全方位方案
    this.userPlanResponse?.allCheckup.packageList.forEach(packageList => {
      const deptName = packageList.deptName;
      packageList.examItemList.forEach(examInfo => {
        examInfo.deptName = deptName;
        this.examInfoGroupByPackage[0].push(examInfo);
      });
    });

    // 把所有在本方案內的檢項 添加至智能專屬方案
    this.userPlanResponse?.aiExclusive.packageList.forEach(packageList => {
      const deptName = packageList.deptName;
      packageList.examItemList.forEach(examInfo => {
        examInfo.deptName = deptName;
        this.examInfoGroupByPackage[1].push(examInfo);
      });
    });

    // 把所有在本方案內的檢項 添加至安心起步方案
    this.userPlanResponse?.basicCheckup.packageList.forEach(packageList => {
      const deptName = packageList.deptName;
      packageList.examItemList.forEach(examInfo => {
        examInfo.deptName = deptName;
        this.examInfoGroupByPackage[2].push(examInfo);
      });
    });

    // 取得所有風險因子(為健康調查表用戶勾選之項目)
    const userPlanRequest: UserPlanRequest | null =
      this.sharedStorageService.get('userPlanRequest') ?? null;
    const riskSet = new Set<string>();
    [
      ...(userPlanRequest?.history ?? []),
      ...(userPlanRequest?.family ?? []),
      ...(userPlanRequest?.subjectiveSymptoms ?? []),
    ].forEach(item => {
      const parts = item.split('_');
      if (parts[1] && parts[1] !== NONE_OF_FOLLOWING) {
        riskSet.add(parts[1]);
      }
    });
    this.allRiskItem = Array.from(riskSet);

    // 初始化每個類別的examInfo收合判斷
    this.isExamInfoGroupByTypeShowAll = Array(
      this.allExamInfoGroupByType.size
    ).fill(false);
  }

  // 取得聚焦評估症狀
  get riskItemsText(): string {
    if (!this.userPlanResponse?.riskItems) return '';

    const andText = this.translateService.instant(
      'reserve-result.risk-hint-and'
    );
    const items = this.userPlanResponse.riskItems;
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return items.join(andText);
    return `<span class="text-highlight">${items.slice(0, -1).join('、')}</span> ${andText} <span class="text-highlight">${items[items.length - 1]}</span>`;
  }

  // 取得"全方位健檢"方案
  get allCheckup(): Package | undefined {
    return this.userPlanResponse?.allCheckup;
  }

  // 取得"智能專屬"方案
  get aiExclusive(): Package | undefined {
    return this.userPlanResponse?.aiExclusive;
  }

  // 取得"基礎健檢"方案
  get basicCheckup(): Package | undefined {
    return this.userPlanResponse?.basicCheckup;
  }

  // 所有examInfo根據examType分組
  get allExamInfoGroupByType(): Map<string, ExamInfo[]> {
    const allExamInfo: ExamInfo[] = [
      ...(this.allCheckup?.packageList ?? []),
      ...(this.aiExclusive?.packageList ?? []),
      ...(this.basicCheckup?.packageList ?? []),
    ].flatMap(pkg =>
      pkg.examItemList.map(exam => ({
        ...exam,
        deptName: pkg.deptName,
      }))
    );

    const result = new Map<string, ExamInfo[]>();
    allExamInfo.forEach(exam => {
      const type = exam.examType;
      if (!type) return;
      if (!result.has(type)) {
        result.set(type, []);
      }
      const list = result.get(type)!;
      if (!list.some(e => e.examItem === exam.examItem)) {
        list.push(exam);
      }
    });

    // 加入排序邏輯
    const order = ['潛在疾病風險檢查建議', '個人歷史異常追蹤', '優先必要檢查項目'];
    const sortedEntries = Array.from(result.entries()).sort(([a], [b]) => {
      const indexA = order.indexOf(a);
      const indexB = order.indexOf(b);

      // 如果某個類型不在預定義順序中，放到最後
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    return new Map(sortedEntries);
  }

  // examInfo對全方位健檢是否checked (需檢查是否在對應的examType分類中)
  isAllCheckupChecked(examInfo: ExamInfo, examType?: string): boolean {
    return this.examInfoGroupByPackage[0].some(
      e => e.examItem === examInfo.examItem && 
           (!examType || e.examType === examType)
    );
  }

  // examInfo對智能專屬是否checked (需檢查是否在對應的examType分類中)
  isAiExclusiveChecked(examInfo: ExamInfo, examType?: string): boolean {
    return this.examInfoGroupByPackage[1].some(
      e => e.examItem === examInfo.examItem && 
           (!examType || e.examType === examType)
    );
  }

  // examInfo對基礎健檢是否checked (需檢查是否在對應的examType分類中)
  isBasicCheckupChecked(examInfo: ExamInfo, examType?: string): boolean {
    return this.examInfoGroupByPackage[2].some(
      e => e.examItem === examInfo.examItem && 
           (!examType || e.examType === examType)
    );
  }

  // 取得全方位檢項數量 index 0: 潛在疾病 1: 個人異常追蹤 2: 標準檢查
  getAllCheckupCountByExamTypeIndex(index: number): number {
    const examTypeArray = Array.from(this.allExamInfoGroupByType.keys());
    const examType = examTypeArray[index];

    switch (examType) {
      case '潛在疾病風險檢查建議':
        return Number(this.allCheckup?.potentialCnt ?? 0);
      case '個人歷史異常追蹤':
        return Number(this.allCheckup?.abnormalCnt ?? 0);
      case '優先必要檢查項目':
        return 0;
      default:
        return 0;
    }
  }

  // 取得智能專屬檢項數量 index 0: 潛在疾病 1: 個人異常追蹤 2: 標準檢查
  getAiExclusiveCountByExamTypeIndex(index: number): number {
    const examTypeArray = Array.from(this.allExamInfoGroupByType.keys());
    const examType = examTypeArray[index];

    switch (examType) {
      case '潛在疾病風險檢查建議':
        return Number(this.aiExclusive?.potentialCnt ?? 0);
      case '個人歷史異常追蹤':
        return Number(this.aiExclusive?.abnormalCnt ?? 0);
      case '優先必要檢查項目':
        return 0;
      default:
        return 0;
    }
  }

  // 取得基礎檢項數量 index 0: 潛在疾病 1: 個人異常追蹤 2: 標準檢查
  getBasicCheckupCountByExamTypeIndex(index: number): number {
    const examTypeArray = Array.from(this.allExamInfoGroupByType.keys());
    const examType = examTypeArray[index];

    switch (examType) {
      case '潛在疾病風險檢查建議':
        return Number(this.basicCheckup?.potentialCnt ?? 0);
      case '個人歷史異常追蹤':
        return Number(this.basicCheckup?.abnormalCnt ?? 0);
      case '優先必要檢查項目':
        return 0;
      default:
        return 0;
    }
  }

  // 開啟/收合examInfo
  toggleExamInfoGroupByTypeShowAll(index: number) {
    this.isExamInfoGroupByTypeShowAll[index] =
      !this.isExamInfoGroupByTypeShowAll[index];
  }

  // 手機版展示檢項dialog
  showExamDialog(examInfo: ExamInfo) {
    const examDialog = this.dialog.open(SingleActionDialogComponent, {
      panelClass: 'reserve-result-dialog',
      data: {
        title: examInfo.examName,
        message: examInfo.examMemo,
        buttonText: this.translateService.instant('common.button.i-understood'),
        showCloseButton: false,
        action: () => examDialog.close(),
      },
    });
  }

  /**
   * 選擇方案
   * @param type 0: 全方位 1: 智能專屬 2: 基礎
   */
  choosePackage(type: number) {
    if (type === 0) {
      this.sharedStorageService.set(
        'selectedPackage',
        this.translateService.instant('reserve-result.all-checkup')
      );
      this.sharedStorageService.set(
        'examItemList',
        this.examInfoGroupByPackage[0]
      );
      this.sharedStorageService.set(
        'packagePrice',
        this.calculatedAllCheckupPrice.toString()
      );
      this.sharedStorageService.set('originalPrice', this.allCheckUpOriginalPrice);
      this.allCheckupDiscountRate = this.allCheckup?.discountRate ?? '1';
      this.sharedStorageService.set(
        'discountRate',
        this.allCheckupDiscountRate
      );
    } else if (type === 1) {
      this.sharedStorageService.set(
        'selectedPackage',
        this.translateService.instant('reserve-result.ai-exclusive')
      );
      this.sharedStorageService.set(
        'examItemList',
        this.examInfoGroupByPackage[1]
      );
      this.sharedStorageService.set(
        'packagePrice',
        this.calculatedAiExclusivePrice.toString()
      );
      this.sharedStorageService.set('originalPrice', this.aiExclusiveOriginalPrice);
      this.aiExclusiveDiscountRate = this.aiExclusive?.discountRate ?? '1';
      this.sharedStorageService.set(
        'discountRate',
        this.aiExclusiveDiscountRate
      );
    } else {
      this.sharedStorageService.set(
        'selectedPackage',
        this.translateService.instant('reserve-result.basic-checkup')
      );
      this.sharedStorageService.set(
        'examItemList',
        this.examInfoGroupByPackage[2]
      );
      this.sharedStorageService.set(
        'packagePrice',
        this.calculatedBasicCheckupPrice.toString()
      );
      this.sharedStorageService.set('originalPrice', this.basicCheckupOriginalPrice);
      this.basicCheckupDiscountRate = this.basicCheckup?.discountRate ?? '1';
      this.sharedStorageService.set(
        'discountRate',
        this.basicCheckupDiscountRate
      );
    }
    this.router.navigate(['/healthcheck/reserve/additem']).then();
  }

  protected readonly Number = Number;

  //滾動對應差異方案 出現固定方案
  currentTableHeaderTitle: string = '';
  isTableFixedActive: boolean = false;
  animationKey = 0;

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const reserveTableBlock = document.querySelector(
      '.reserve-table-block'
    ) as HTMLElement;
    const tableHeaders = this.getTableHeaders();
    let activeTitle = '';

    if (reserveTableBlock) {
      const blockPosition = reserveTableBlock.getBoundingClientRect();
      const offset = window.innerWidth <= 768 ? -24 : -24;
      this.isTableFixedActive =
        blockPosition.top <= offset && blockPosition.bottom > offset;
    }

    tableHeaders.forEach((header: HTMLElement) => {
      const headerPosition = header.getBoundingClientRect();
      const offset = window.innerWidth <= 768 ? 176 : 162;
      if (headerPosition.top <= offset && headerPosition.bottom > offset) {
        const titleElement = header.querySelector(
          '.plan-table-header-title'
        ) as HTMLElement;
        activeTitle = titleElement?.textContent?.trim() || '';
      }
    });

    // 加 key 值来觸發動畫
    if (this.currentTableHeaderTitle !== activeTitle) {
      this.currentTableHeaderTitle = activeTitle;
      this.animationKey++; //
    }
  }

  private getTableHeaders(): HTMLElement[] {
    return Array.from(
      document.querySelectorAll('.plan-table')
    ) as HTMLElement[];
  }

  //風險因子橫向拖曳
  isDragging = false;
  startX = 0;
  scrollLeft = 0;

  onMouseDown(event: MouseEvent): void {
    const scrollElement = event.target as HTMLElement;
    this.isDragging = true;
    this.startX = event.pageX - scrollElement.offsetLeft;
    this.scrollLeft = scrollElement.scrollLeft;

    scrollElement.addEventListener('mousemove', this.onMouseMove.bind(this));
    scrollElement.addEventListener('mouseup', this.onMouseUp.bind(this));
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const scrollElement = event.target as HTMLElement;
    const x = event.pageX - scrollElement.offsetLeft;
    const walk = (x - this.startX) * 2; // 調整滾動速度
    scrollElement.scrollLeft = this.scrollLeft - walk;
  }

  onMouseUp(event: MouseEvent): void {
    const scrollElement = event.target as HTMLElement;
    this.isDragging = false;
    scrollElement.removeEventListener('mousemove', this.onMouseMove.bind(this));
    scrollElement.removeEventListener('mouseup', this.onMouseUp.bind(this));
  }

  /**
   * 計算方案總價格（每個檢項先套用折扣並無條件十進位，再加總）
   */
  private calculatePackageTotalPrice(packageData: Package): number {
    let discountRate = Number(packageData.discountRate) || 1;
    // if (packageData == this.allCheckup) {
    //   discountRate = Number(this.allCheckupDiscountRate);
    // } else if (packageData == this.aiExclusive) {
    //   discountRate = Number(this.aiExclusiveDiscountRate);
    // } else if (packageData == this.basicCheckup) {
    //   discountRate = Number(this.basicCheckupDiscountRate);
    // } else {
    //   discountRate = 0;
    // }

    return packageData.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);

        // 檢查 rateStatus，為 true 才套用折扣
        if (examInfo.rateStatus === true) {
          let discountedPrice = originalPrice.mul(discountRate);
          let roundedPrice = discountedPrice.div(10).ceil().mul(10); // 無條件進位到十位數
          // 若有活動折扣率
          if (this.partyDiscountRate) {
            roundedPrice = roundedPrice.mul(new Decimal(this.partyDiscountRate));
            // 四捨五入到個位整數
            roundedPrice = new Decimal(Math.round(roundedPrice.toNumber()));
          }
          return sum + roundedPrice.toNumber();
        } else {
          // rateStatus 為 false 時直接加原價
          return sum + originalPrice.toNumber();
        }
      }, 0);
      return total + packageTotal;
    }, 0);
  }

  // 取得全方位方案價格
  get calculatedAllCheckupPrice(): number {
    return this.allCheckup
      ? this.calculatePackageTotalPrice(this.allCheckup)
      : 0;
  }

  // 取得智能專屬方案價格
  get calculatedAiExclusivePrice(): number {
    return this.aiExclusive
      ? this.calculatePackageTotalPrice(this.aiExclusive)
      : 0;
  }

  // 取得基礎方案價格
  get calculatedBasicCheckupPrice(): number {
    return this.basicCheckup
      ? this.calculatePackageTotalPrice(this.basicCheckup)
      : 0;
  }


  // 取得全方位方案原始價格
  get allCheckUpOriginalPrice(): number {
    return this.allCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0;
  }

  // 取得智能專屬方案原始價格
  get aiExclusiveOriginalPrice(): number {
    return this.aiExclusive?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0;
  }

  // 取得基礎方案原始價格
  get basicCheckupOriginalPrice(): number {
    return this.basicCheckup?.packageList.reduce((total, packageInfo) => {
      const packageTotal = packageInfo.examItemList.reduce((sum, examInfo) => {
        const originalPrice = new Decimal(examInfo.examPrice) || new Decimal(0);
        return sum + originalPrice.toNumber();
      }, 0);
      return total + packageTotal;
    }, 0) ?? 0;
  }

  // 是否為特殊活動
  get isSpecialHealthPlan(): boolean {
    return this.healthPlan === 'special';
  }

  // CB-045 特殊活動 - 下載個人化方案比較表
  downloadSpecialUserPlan() {

    // 檢查方案演算是否超過 30 分鐘
    const calculationTime = this.sharedStorageService.get('planCalculationStartTime');
    if (!calculationTime || this.idleTimeoutService.isPlanCalculationExpired(calculationTime)) {
      this.toastr.warning(this.translateService.instant('reserve-result.download-healthPlan-usual-hint'), '', {
        toastClass: 'ngx-toastr toast-info',
      });
      return;
    }

    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    const userPlanResponse = this.sharedStorageService.get('userPlanResponse');

    const request: DownloadUserPlanRequest = {
      orgNo: userPlanRequest?.orgNo ?? '',
      idNo: userPlanRequest?.idNo ?? '',
      gender: userPlanRequest?.gender ?? '',
      birthday: userPlanRequest?.birthday ?? '',
      riskItems: this.allRiskItem,
    }
    this.downloadUserPlanService.downloadUserPlan(request).subscribe({
      next: (resp) => {
        const blob = resp.body as Blob;
        const cd = resp.headers.get('content-disposition');
        
        // 解析檔名
        let fileName = filenameFromContentDisposition(cd);
        if (!fileName) {
          const orgNo = userPlanRequest?.orgNo ?? '';
          const centerName = this.centerMapping[orgNo] || orgNo;
          fileName = `${moment().format('YYYYMMDD')}-健檢方案比較表-${centerName}中心.pdf`;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        const dialogRef = this.dialog.open(SingleActionDialogComponent, {
          panelClass: 'reserve-result-dialog',
          disableClose: true,
          data: {
            title: this.translateService.instant(
              'reserve-result.download-healthPlan-title'
            ),
            message: this.translateService.instant(
              'reserve-result.download-healthPlan-message'
            ),
            buttonText: this.translateService.instant('reserve-result.download-confirm'),
            showCloseButton: false,
            action: () => {
              console.log("下載個人化方案比較表");
              dialogRef.close();
            },
          },
        });


      },
      error: async (err) => {
        console.error('下載 API 發生錯誤:', err);
        // 將 Blob 轉換為 JSON 以取得 errorMessage
        let errorMessage = '下載失敗,請稍後再試';
        if (err.error instanceof Blob) {
          try {
            const text = await err.error.text();
            const errorObj = JSON.parse(text);
            errorMessage = errorObj.errorMessage || errorMessage;
          } catch (parseError) {
            console.error('解析錯誤訊息失敗:', parseError);
          }
        } else if (err.error?.errorMessage) {
          errorMessage = err.error.errorMessage;
        }
        this.toastr.error(errorMessage);
      }
    });
  }


  // 下載個人化方案比較表
  downloadUserPlan() {

    // 檢查方案演算是否超過 30 分鐘
    const calculationTime = this.sharedStorageService.get('planCalculationStartTime');
    if (!calculationTime || this.idleTimeoutService.isPlanCalculationExpired(calculationTime)) {
      this.toastr.warning(this.translateService.instant('reserve-result.download-healthPlan-usual-hint'), '', {
        toastClass: 'ngx-toastr toast-info',
      });
      return;
    }

    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    const userPlanResponse = this.sharedStorageService.get('userPlanResponse');

    const request: DownloadUserPlanRequest = {
      orgNo: userPlanRequest?.orgNo ?? '',
      idNo: userPlanRequest?.idNo ?? '',
      gender: userPlanRequest?.gender ?? '',
      birthday: userPlanRequest?.birthday ?? '',
      riskItems: this.allRiskItem,
    }
    this.downloadUserPlanService.downloadUserPlan(request).subscribe({
      next: (resp) => {
        const blob = resp.body as Blob;
        const cd = resp.headers.get('content-disposition');
        
        // 解析檔名
        let fileName = filenameFromContentDisposition(cd);
        if (!fileName) {
          const orgNo = userPlanRequest?.orgNo ?? '';
          const centerName = this.centerMapping[orgNo] || orgNo;
          fileName = `${moment().format('YYYYMMDD')}-健檢方案比較表-${centerName}中心.pdf`;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: async (err) => {
        console.error('下載 API 發生錯誤:', err);
        // 將 Blob 轉換為 JSON 以取得 errorMessage
        let errorMessage = '下載失敗,請稍後再試';
        if (err.error instanceof Blob) {
          try {
            const text = await err.error.text();
            const errorObj = JSON.parse(text);
            errorMessage = errorObj.errorMessage || errorMessage;
          } catch (parseError) {
            console.error('解析錯誤訊息失敗:', parseError);
          }
        } else if (err.error?.errorMessage) {
          errorMessage = err.error.errorMessage;
        }
        this.toastr.error(errorMessage);
      }
    });
  }

  // 開啟手機前鏡頭
  async openCamera() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }, // 前鏡頭
        audio: false,
      });
      this.video.nativeElement.srcObject = this.stream;
    } catch (error) {
      console.error('無法開啟鏡頭:', error);
    }
  }


  // 關閉手機前鏡頭
  stopCamera() {
    if (this.stream) {
      const tracks = this.stream.getTracks();
      tracks.forEach(track => track.stop());
      this.stream = null;
      this.video.nativeElement.srcObject = null;
    }
  }

  // 判斷風險因子是否需要靠左對齊(根據螢幕尺寸和數量)
  get shouldAlignStart(): boolean {
    const count = this.allRiskItem.length;
    const width = window.innerWidth;

    // 大螢幕(>= 1200px): 10個以上靠左
    if (width >= 1200) return count >= 10;

    // 平板(768px ~ 1199px): 7個以上靠左
    if (width >= 768 && width < 1200) return count >= 7;

    // 手機(< 768px): 4個以上靠左
    return count >= 5;
  }

  // 據點轉換
  private centerMapping: { [key: string]: string } = {
    '01': '敦南',
    '02': '內湖',
    '04': '新竹',
    '05': '台南',
  };
}
